import type { ElementInfo } from './types';
import { isVisible, roleOf, textOf } from './dom';
import { isPrivate, redact } from './redact';
import { log } from './logger';

export const IDX_ATTR = 'data-agent-idx';

const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="searchbox"]',
  '[role="textbox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
].join(',');

const LANDMARK_SELECTOR = 'h1, h2, h3, [role="heading"], [role="alert"], [role="status"]';

const MAX_ELEMENTS = 300;

let elements: Element[] = [];
let descriptors: { tag: string; role: string; text: string }[] = [];

function rect(el: Element): [number, number, number, number] {
  const r = el.getBoundingClientRect();
  return [Math.round(r.left), Math.round(r.top), Math.round(r.width), Math.round(r.height)];
}

function shouldSkip(
  el: Element,
  sdkRoot: Element | null,
  extraPrivate: readonly string[],
): boolean {
  if (sdkRoot && (el === sdkRoot || sdkRoot.contains(el))) return true;
  if (isPrivate(el, extraPrivate)) return true;
  return !isVisible(el);
}

interface ExtractOptions {
  sdkRoot?: Element | null;
  redactSelectors?: readonly string[];
  doc?: Document;
}

export function extractElements(options: ExtractOptions = {}): ElementInfo[] {
  const doc = options.doc ?? document;
  const sdkRoot = options.sdkRoot ?? null;
  const extraPrivate = options.redactSelectors ?? [];

  for (const stale of Array.from(doc.querySelectorAll(`[${IDX_ATTR}]`))) {
    stale.removeAttribute(IDX_ATTR);
  }

  const candidates = new Set<Element>();
  for (const el of Array.from(doc.querySelectorAll(INTERACTIVE_SELECTOR))) candidates.add(el);
  for (const el of Array.from(doc.querySelectorAll(LANDMARK_SELECTOR))) candidates.add(el);

  const map: ElementInfo[] = [];
  elements = [];
  descriptors = [];

  for (const el of candidates) {
    if (map.length >= MAX_ELEMENTS) break;
    if (shouldSkip(el, sdkRoot, extraPrivate)) continue;

    const text = redact(textOf(el));
    const role = roleOf(el);
    if (!text && role === 'generic') continue;

    const index = map.length + 1;
    el.setAttribute(IDX_ATTR, String(index));
    elements[index] = el;
    descriptors[index] = { tag: el.tagName, role, text };
    map.push({ index, role, text, bbox: rect(el) });
  }

  log.debug(`extracted ${map.length} elements`);
  return map;
}

export function elementForIndex(index: number): Element | null {
  const cached = elements[index];
  if (cached?.isConnected) return cached;

  const found = document.querySelector(`[${IDX_ATTR}="${index}"]`);
  if (found) {
    elements[index] = found;
    return found;
  }
  return recoverByDescriptor(index);
}

/**
 * A framework re-render replaces DOM nodes, and the replacements carry neither
 * our attribute nor the identity we cached. Re-find the element by what it looked
 * like, but only when exactly one candidate matches — an ambiguous match is a
 * failure rather than a guess, because guessing means acting on the wrong thing.
 */
function recoverByDescriptor(index: number): Element | null {
  const want = descriptors[index];
  if (!want || !want.text) return null;

  let candidates: Element[];
  try {
    candidates = Array.from(document.querySelectorAll(want.tag));
  } catch {
    return null;
  }

  const matches = candidates.filter(
    (el) => el.isConnected && roleOf(el) === want.role && redact(textOf(el)) === want.text,
  );
  const recovered = matches.length === 1 ? matches[0] : undefined;
  if (!recovered) return null;

  recovered.setAttribute(IDX_ATTR, String(index));
  elements[index] = recovered;
  log.debug(`recovered element ${index} after a host re-render`);
  return recovered;
}

export function describeIndex(index: number): string {
  const el = elementForIndex(index);
  if (!el) return `element ${index}`;
  const text = textOf(el);
  return text ? `${roleOf(el)} “${text}”` : `${roleOf(el)} ${index}`;
}
